export enum FreightOrigin {
  AUTOMATIC = 'AUTOMATIC',
  MANUAL = 'MANUAL',
}

import { isNil } from 'ramda';
import { BusinessPolicyVoucherDTO } from '../../../services/modules/business-policies/businessPolicies.types';
import { Local } from '../../../services/modules/locals/locals.types';
import { ShippingCompany } from '../../../services/modules/shippingCompanies/shippingCompanies.types';
import { FreightType } from '../../../types/Enums';
import { ListCustomerAddressDTO } from '../../../types/address.types';
import { BaseDTO } from '../../../utils/base.dto';
import { Collection } from '../../collections/collections.types';
import { CommercialPolicyDTO } from '../../commercial-policies/commercialPolicies.types';
import { BrandFreightType } from '../../freight-type/brand-freight-type.types';
import { PaymentMethod } from '../../payment-method/payment-method.types';
import { DeliveryWindow } from '../../products/ProductTypes';
import { OrderClassification, OrderClassificationType } from '../order.types';
import { SaleType } from './interfaces/orderCondition.interface';

export interface OrderConditionSubmitData {
  commercialDiscount?: number | null;
  deliveryAddressId?: string | null;
  deliveryWindowId?: string | null;
  deliveryWindowIds?: string[] | null;
  id?: string | null;
  orderConditionsIds?: string[] | null;
  internalObservation?: string | null;
  observation?: string | null;
  orderClassifications?: { code: string; typeCode: string }[];
  paymentId?: string | null;
  branchId?: string | null;
  shippingCompanyId?: string | null;
  brandFreightTypeId?: string | null;
  emitInvoice?: boolean;
  orderCollectionId?: string | null;
  commercialPolicyId?: string | null;
  orderBatchItemCode?: number | null;
  orderOperationCode?: number | null;
  orderChargeTypeCode?: number | null;
  businessPolicyIds?: string[] | null;
  saleTypeId?: string | null;
  shippingCostValue?: number | null;
  billingForecastDate?: Date | null;
}

const resolveShippingCompanyId = (data: OrderConditionFormData): string | null => {
  if (data?.brandFreightType?.code === FreightType.NO_TRANSPORT_OCCURENCE) {
    return null;
  }

  return data?.shippingCompany?.id || null;
};

export class OrderConditionFormData extends BaseDTO<OrderConditionFormData> {
  commercialDiscount?: number;
  emitInvoice?: boolean;
  deliveryAddress?: ListCustomerAddressDTO;
  deliveryWindow?: DeliveryWindow;
  brandFreightType?: BrandFreightType;
  id: string = '';
  internalObservation?: string;
  observation?: string;
  orderClassification?: OrderClassification;
  orderClassificationType?: OrderClassificationType;
  orderDeliveryWindows?: DeliveryWindow[];
  collection?: Collection;
  payment?: PaymentMethod;
  branch?: Local;
  shippingCompany?: ShippingCompany;
  commercialPolicy?: CommercialPolicyDTO;
  orderBatchItemCode?: number;
  orderOperationCode?: number;
  orderChargeTypeCode?: number | string;
  businessPolicies?: BusinessPolicyVoucherDTO[];
  saleType?: SaleType;
  billingForecastDate?: Date | null;

  shippingCostValue?: number;
  // biome-ignore lint/suspicious/noExplicitAny: used in custom order conditions
  [key: string]: any;

  static toConditionDTO(
    data: OrderConditionFormData,
    orderConditionId: string,
  ): Partial<OrderConditionSubmitData> {
    const shippingCostValue = data.shippingCostValue as number | string | undefined;

    const dto = {
      id: orderConditionId,
      deliveryAddressId: data?.deliveryAddress?.address?.id || data?.deliveryAddress?.id || null,
      deliveryWindowId: data?.deliveryWindow?.id || null,
      paymentId: data?.payment?.id || null,
      observation: data?.observation,
      internalObservation: data?.internalObservation,
      commercialDiscount: data.commercialPolicy?.discount || null,
      commercialPolicyId: data.commercialPolicy?.id || null,
      businessPolicyIds: data.businessPolicies?.map((bp) => bp.id),
      shippingCostValue:
        isNil(shippingCostValue) || shippingCostValue === '' ? null : +shippingCostValue,
      billingForecastDate: data.billingForecastDate,
    };

    return dto;
  }

  static toCustomDefinitionDTO(
    data: OrderConditionFormData,
    orderConditionId: string,
  ): Partial<OrderConditionSubmitData> {
    return {
      id: orderConditionId,
      branchId: data?.branch?.id ?? null,
      shippingCompanyId: resolveShippingCompanyId(data),
      brandFreightTypeId: data?.brandFreightType?.id || null,
      emitInvoice: data?.emitInvoice,
      orderCollectionId: data.collection?.id || null,
      orderBatchItemCode: data.orderBatchItemCode || null,
      orderOperationCode: data.orderOperationCode,
      orderChargeTypeCode: isNil(data.orderChargeTypeCode)
        ? null
        : Number(data.orderChargeTypeCode),
      orderClassifications:
        data.orderClassification && data.orderClassificationType
          ? [
              {
                code: data.orderClassification.code,
                typeCode: data.orderClassificationType.code,
              },
            ]
          : [],
      saleTypeId: data?.saleType?.id || null,
      paymentId: data.payment?.id || null,
    };
  }
}

export interface OrderConditionBusinessPolicyVoucher extends BusinessPolicyVoucherDTO {
  applicationDate?: Date | string | null;
}
